import { useEffect, useMemo, useRef, useState } from 'react';
import { LogOut, Menu, X } from 'lucide-react';
import { cn } from '../lib/utils';

// Dock-style icon-only nav rail: hover magnifies the nearest icon and
// ripples out to its neighbors (macOS Dock physics — a wave, not a single
// pop), and shows the item's label in a flyout instead of permanent text.
// Items are clustered into dividered groups via their `group` field (see
// SUPER_ADMIN_MENU in Sidebar.jsx). A hamburger toggle at the top opens a
// second surface — a full labeled menu ("curtain") for when icon-only
// isn't enough — without changing the rail itself.
function clusterByGroup(items) {
  const groups = [];
  let current = null;
  items.forEach((item) => {
    if (!current || current.key !== item.group) {
      current = { key: item.group, items: [item] };
      groups.push(current);
    } else {
      current.items.push(item);
    }
  });
  return groups.map((g) => g.items);
}

const GROUP_LABELS = {
  oversight: 'Oversight',
  content: 'Content',
  platform: 'Platform',
};

// Dock magnify: a Gaussian falloff centered on the cursor so 2-3 neighbors
// ripple in and out as it moves, rather than only the nearest icon popping.
const MAGNIFY_PEAK = 1.63;
const MAGNIFY_SIGMA = 34;

export default function SidebarIconRail({ items, isActive, onNavigate, user, onLogout, topExtra }) {
  const asideRef = useRef(null);
  const railRef = useRef(null);
  const curtainRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [curtainOpen, setCurtainOpen] = useState(false);
  const groups = useMemo(() => clusterByGroup(items), [items]);

  const navigate = (path) => {
    setCurtainOpen(false);
    onNavigate(path);
  };

  // The flyout renders outside `nav` so it isn't clipped — its position is
  // measured against the aside's box, not the rail's.
  const handleMouseMove = (e) => {
    const rail = railRef.current;
    const asideEl = asideRef.current;
    if (!rail || !asideEl) return;
    const asideRect = asideEl.getBoundingClientRect();
    const buttons = rail.querySelectorAll('[data-rail-btn]');
    let closest = null;
    let closestDist = Infinity;
    let closestTop = 0;
    let closestLabel = '';
    buttons.forEach((btn) => {
      const r = btn.getBoundingClientRect();
      const centerY = r.top + r.height / 2;
      const dist = Math.abs(e.clientY - centerY);
      const scale = 1 + (MAGNIFY_PEAK - 1) * Math.exp(-(dist * dist) / (2 * MAGNIFY_SIGMA * MAGNIFY_SIGMA));
      btn.style.transform = `scale(${scale}) translateX(${(scale - 1) * 8}px)`;
      if (dist < closestDist) {
        closestDist = dist;
        closest = btn;
        closestTop = r.top - asideRect.top;
        closestLabel = btn.dataset.label;
      }
    });
    if (closest && closestDist < 18) {
      setHover({ label: closestLabel, top: closestTop });
    } else {
      setHover(null);
    }
  };

  const handleMouseLeave = () => {
    const rail = railRef.current;
    if (rail) {
      rail.querySelectorAll('[data-rail-btn]').forEach((btn) => {
        btn.style.transform = 'scale(1)';
      });
    }
    setHover(null);
  };

  // Close the curtain on Escape, or on a click outside both the curtain
  // and its toggle button.
  useEffect(() => {
    if (!curtainOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setCurtainOpen(false);
    };
    const onClick = (e) => {
      if (curtainRef.current && !curtainRef.current.contains(e.target) && !e.target.closest('[data-curtain-toggle]')) {
        setCurtainOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClick);
    };
  }, [curtainOpen]);

  return (
    <>
      <aside
        ref={asideRef}
        className="fixed left-0 top-0 h-full w-12 bg-teal-900 flex flex-col items-center py-3 z-50"
        data-testid="app-sidebar"
      >
        <button
          type="button"
          data-curtain-toggle
          onClick={() => setCurtainOpen((v) => !v)}
          aria-label={curtainOpen ? 'Close menu' : 'Open full menu'}
          aria-expanded={curtainOpen}
          data-testid="sidebar-curtain-toggle"
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mb-2 text-slate-400 hover:text-white hover:bg-teal-800"
        >
          {curtainOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
        <div className="w-5 h-px bg-teal-700 mb-2" aria-hidden="true" />

        <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mb-3">
          <span className="text-white font-bold text-sm">F</span>
        </div>

        {topExtra && <div className="mb-2 flex-shrink-0">{topExtra}</div>}

        <nav
          ref={railRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="flex-1 flex flex-col items-center overflow-visible py-1 w-full"
        >
          {groups.map((group, gi) => (
            <div key={group[0]?.id || gi} className="flex flex-col items-center gap-1">
              {group.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-rail-btn
                    data-label={item.readOnly ? `${item.label} · View only` : item.label}
                    onClick={() => navigate(item.path)}
                    aria-label={item.label}
                    data-testid={`sidebar-${item.id}`}
                    className={cn(
                      'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-100',
                      active ? 'bg-teal-700/50 text-white' : 'text-slate-400 hover:text-white hover:bg-teal-800'
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                  </button>
                );
              })}
              {gi < groups.length - 1 && <div className="w-5 h-px bg-teal-700 my-2" aria-hidden="true" />}
            </div>
          ))}
        </nav>

        {hover && !curtainOpen && (
          <div
            className="absolute left-14 bg-teal-950 text-white text-base font-medium px-3 py-2 rounded-md whitespace-nowrap pointer-events-none shadow-lg z-10"
            style={{ top: hover.top }}
          >
            {hover.label}
          </div>
        )}

        <div className="mt-auto flex flex-col items-center gap-2 pt-2 border-t border-teal-800 w-full flex-shrink-0">
          <div
            className="w-8 h-8 bg-teal-800 rounded-full flex items-center justify-center"
            title={user?.username || 'User'}
          >
            <span className="text-white text-xs font-medium">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-teal-800"
            title="Sign out"
            aria-label="Sign out"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Backdrop — dims the page while the curtain is open, click to close */}
      <div
        aria-hidden="true"
        onClick={() => setCurtainOpen(false)}
        className={cn(
          'fixed inset-0 bg-[#020F24]/45 z-40 transition-opacity duration-200',
          curtainOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
      />

      {/* Curtain — full labeled menu, grows down from the top-left */}
      <div
        ref={curtainRef}
        role="menu"
        aria-hidden={!curtainOpen}
        data-testid="sidebar-curtain"
        className={cn(
          'fixed left-12 top-0 w-72 max-h-full overflow-y-auto bg-white shadow-2xl z-50 origin-top transition-transform duration-200 ease-out rounded-br-xl',
          curtainOpen ? 'scale-y-100' : 'scale-y-0 pointer-events-none'
        )}
      >
        <div className="py-2">
          {groups.map((group, gi) => (
            <div key={group[0]?.id || gi}>
              {GROUP_LABELS[group[0]?.group] && (
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-4 pt-2.5 pb-1">
                  {GROUP_LABELS[group[0].group]}
                </p>
              )}
              {group.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    onClick={() => navigate(item.path)}
                    data-testid={`sidebar-curtain-${item.id}`}
                    className={cn(
                      'group flex items-center gap-2.5 w-[calc(100%-16px)] mx-2 px-2.5 py-2 rounded-lg text-left transition-transform duration-100 origin-left hover:scale-[1.045]',
                      active ? 'bg-teal-50' : 'hover:bg-slate-50'
                    )}
                  >
                    <span
                      className={cn(
                        'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
                        active ? 'bg-teal-900 text-white' : 'bg-slate-100 text-slate-600'
                      )}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                    </span>
                    <span className={cn('text-sm font-medium truncate', active ? 'text-teal-900' : 'text-slate-800')}>
                      {item.label}
                    </span>
                    {item.readOnly && (
                      <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">View only</span>
                    )}
                  </button>
                );
              })}
              {gi < groups.length - 1 && <div className="h-px bg-slate-200 mx-4 my-2" aria-hidden="true" />}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
